<template>
  <DetailTemplate :custom-class="'review-detail detail-page'" v-if="getPermission('reviews:view')">
    <template v-slot:sidebar-detail>
      <div>
        <!-- Rating Display -->
        <div class="mt-2" v-if="reviewData.rating">
          <v-rating
            :value="reviewData.rating"
            readonly
            dense
            background-color="orange lighten-3"
            color="orange"
          ></v-rating>
        </div>
      </div>

      <div class="mt-5">
        <table width="100%">
          <tr>
            <td
              valign="top"
              style="width: 95px"
              class="py-1 font-size-18 font-weight-500"
            >
              Review #
            </td>
            <td valign="middle" class="py-1 font-size-18">
              <label class="m-0" v-if="reviewData.id">
                <v-chip small color="cyan" text-color="white" label>
                  RW-{{ String(reviewData.id).padStart(4, "0") }}
                </v-chip>
              </label>
            </td>
          </tr>
          <tr>
            <td
              valign="top"
              style="width: 95px"
              class="py-1 font-size-18 font-weight-500"
            >
              Job #
            </td>
            <td valign="middle" class="py-1 font-size-18">
              <!-- Single visit (like your original code) -->

              <div v-if="filteredVisits.length" class="d-flex flex-wrap">
                <label
                  v-for="(visit, index) in filteredVisits"
                  :key="`visit-${index}`"
                  class="mr-1 mb-1"
                >
                  <Barcode :barcode="visit.barcode"></Barcode>
                </label>
              </div>
              <em v-else class="text-muted">No job visits available</em>

            </td>
          </tr>
          <tr>
            <td valign="top" class="py-1 font-size-18 font-weight-500">
              Customer
            </td>
            <td valign="middle" class="py-1 font-size-18">
              <router-link
                v-if="reviewData.customer && reviewData.customer.id"
                :to="
                  getDefaultRoute('customer.detail', {
                    params: {
                      id: reviewData.customer.id,
                    },
                  })
                "
              >
                <v-chip
                  small
                  color="blue darken-4"
                  class="cursor-pointer"
                  outlined
                  label
                >
                  {{ reviewData.customer.display_name }}
                </v-chip>
              </router-link>
            </td>
          </tr>
          <tr>
            <td valign="top" class="py-1 font-size-18 font-weight-500">
              Submitted Date
            </td>
            <td valign="middle" class="py-1 font-size-18">
              <span style="color: #24326d !important">
                {{ formatDate(reviewData.added_at) }}
                {{ formatDateStartTime(reviewData.added_at) }}
              </span>
            </td>
          </tr>
          <tr>
            <td valign="middle" class="py-1 font-size-18 font-weight-500">
              Rating
            </td>
            <td valign="middle" class="py-1 font-size-18">
              <v-chip
                small
                :color="getRatingColor(reviewData.rating)"
                text-color="white"
                label
                v-if="reviewData.rating"
              >
                {{ reviewData.rating }}/5 Stars
              </v-chip>
            </td>
          </tr>
          <tr>
            <td valign="top" class="py-1 font-size-18 font-weight-500">
              Punctuality
            </td>
            <td valign="middle" class="py-1 font-size-18">
              <v-chip
                small
                :color="reviewData.punctual_professional ? 'green' : 'red'"
                text-color="white"
                label
                v-if="reviewData.punctual_professional !== null"
              >
                {{ reviewData.punctual_professional ? "Yes" : "No" }}
              </v-chip>
              <em v-else class="text-muted">Not rated</em>
            </td>
          </tr>
          <tr>
            <td valign="top" class="py-1 font-size-18 font-weight-500">
              Cleaning Satisfaction
            </td>
            <td valign="middle" class="py-1 font-size-18">
              <v-chip
                small
                :color="reviewData.cleaning_satisfaction ? 'green' : 'red'"
                text-color="white"
                label
                v-if="reviewData.cleaning_satisfaction !== null"
              >
                {{ reviewData.cleaning_satisfaction ? "Yes" : "No" }}
              </v-chip>
              <em v-else class="text-muted">Not rated</em>
            </td>
          </tr>
          <tr>
            <td valign="top" class="py-1 font-size-18 font-weight-500">
              Would Recommend
            </td>
            <td valign="middle" class="py-1 font-size-18">
              <v-chip
                small
                :color="reviewData.recommend ? 'green' : 'red'"
                text-color="white"
                label
                v-if="reviewData.recommend !== null"
              >
                {{ reviewData.recommend ? "Yes" : "No" }}
              </v-chip>
              <em v-else class="text-muted">Not answered</em>
            </td>
          </tr>
        </table>
      </div>

      <!-- Job Information Section -->
      <div class="mt-5" v-if="reviewData.ticket">
        <v-flex md12>
          <table width="100%">
            <tr>
              <th
                colspan="3"
                class="grey lighten-4 font-size-19 color-custom-blue py-1"
              >
                Job Information
              </th>
            </tr>
            <tr>
              <td
                valign="middle"
                width="100px"
                class="py-1 font-size-18 font-weight-500"
              >
                Title
              </td>
              <td align="center" class="py-1" width="20px">:</td>
              <td valign="middle" class="py-1 font-size-18">
                <label class="m-0">
                  {{ reviewData.ticket.title || "N/A" }}
                </label>
              </td>
            </tr>
            <tr v-if="reviewData.ticket.scheduled_start">
              <td valign="middle" class="py-1 font-size-18 font-weight-500">
                Start Date
              </td>
              <td align="center" class="py-1">:</td>
              <td valign="middle" class="py-1 font-size-18">
                <label class="m-0">
                  {{ formatDate(reviewData.ticket.scheduled_start) }}
                  {{ formatDateStartTime(reviewData.ticket.scheduled_start) }}
                </label>
              </td>
            </tr>
            <tr v-if="reviewData.ticket.scheduled_end">
              <td valign="middle" class="py-1 font-size-18 font-weight-500">
                End Date
              </td>
              <td align="center" class="py-1">:</td>
              <td valign="middle" class="py-1 font-size-18">
                <label class="m-0">
                  {{ formatDate(reviewData.ticket.scheduled_end) }}
                  {{ formatDateStartTime(reviewData.ticket.scheduled_end) }}
                </label>
              </td>
            </tr>
          </table>
        </v-flex>
      </div>

      <!-- Site Location -->
      <div class="mt-5">
        <v-flex md12>
          <table width="100%">
            <tr>
              <th
                class="grey lighten-4 font-size-16 text-uppercase color-custom-blue py-1"
              >
                Site Location
              </th>
            </tr>
            <tr>
              <td class="py-0 font-size-18 py-2">
                <template
                  v-if="
                    reviewData.ticket &&
                    (reviewData?.ticket?.property?.property_name ||
                      reviewData?.ticket?.property?.property_address)
                  "
                >
                  <div
                    class="custom-flex-d"
                    v-if="reviewData?.ticket?.property?.property_name"
                  >
                    <b>
                      <v-icon
                        style="font-size: 18px; margin-top: -3px"
                        class="cmt-2"
                      >
                        mdi-home-map-marker
                      </v-icon>
                    </b>
                    <span>{{ reviewData?.ticket?.property?.property_name }}<br /></span>
                  </div>
                  <div v-else>
                    <em class="text-muted">
                      <v-icon
                        style="font-size: 18px; margin-top: -3px"
                        class="cmt-2"
                      >
                        mdi-home-map-marker
                      </v-icon>
                      No Site Name
                    </em>
                  </div>
                  <div class="custom-flex-d">
                    <b>
                      <v-icon
                        style="font-size: 18px; margin-top: -3px"
                        class="cmt-2"
                      >
                        mdi-map-marker
                      </v-icon>
                    </b>
                    <span>
                      {{
                        reviewData.ticket.property.property_address ||
                        "No address available"
                      }}
                    </span>
                  </div>
                </template>

                <template v-else>
                  <em class="text-muted">
                    <v-icon
                      style="font-size: 18px; margin-top: -3px"
                      class="cmt-2"
                    >
                      mdi-home-map-marker
                    </v-icon>
                    Site not available right now
                  </em>
                </template>
              </td>
            </tr>

            <tr>
              <th
                class="grey lighten-4 font-size-16 text-uppercase color-custom-blue py-1"
              >
                Contact details
              </th>
            </tr>
            <tr v-if="reviewData.customer">
              <td class="py-0 font-size-18">
                <template v-if="reviewData.customer.display_name">
                  {{ reviewData.customer.display_name }}
                </template>
              </td>
            </tr>
            <tr v-if="reviewData.customer">
              <td class="py-0 font-size-18">
                <template v-if="reviewData.customer.primary_phone">
                  {{ reviewData.customer.primary_phone }}
                </template>
              </td>
            </tr>
            <tr v-if="reviewData.customer">
              <td class="py-0 font-size-18">
                <template v-if="reviewData.customer.primary_email">
                  {{ reviewData.customer.primary_email }}
                </template>
              </td>
            </tr>
          </table>
        </v-flex>
      </div>
    </template>

    <template v-slot:header-title>
      <h1
        class="form-title custom-nowrap-ellipsis text-capitalize mr-2 font-weight-700"
        v-if="reviewData.ticket && reviewData.ticket.title"
      >
        Review: RW-{{ String(reviewData.id).padStart(4, "0") }} /
        {{ reviewData.ticket.title }}
      </h1>
    </template>

    <template v-slot:header-action>
      <div class="d-flex align-center justify-end flex-wrap" v-if="pageLoaded">
        <!-- Rating Display in Header -->
        <!-- <v-rating
          v-if="reviewData.rating"
          readonly
          dense
          v-model="reviewData.rating"
          background-color="orange lighten-3"
          color="orange"
        ></v-rating> -->

        <!-- Action Buttons -->
        <v-menu
          transition="slide-y-transition"
          bottom
          content-class="custom-menu-list"
          offset-y
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <!-- <v-btn
              depressed
              :disabled="statusLoading"
              class="mx-2 mb-1 custom-bold-button white--text"
              color="cyan"
              v-bind="attrs"
              v-on="on"
            >
              More... <v-icon right>mdi-chevron-down</v-icon>
            </v-btn> -->
          </template>
          <v-list>
            <!-- <v-list-item
              link
              v-on:click="editReview()"
              :disabled="statusLoading"
            >
              <v-list-item-icon class="margin-auto-zero mr-3 my-2">
                <v-icon class="icon-default-blue">mdi-pencil</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="font-weight-500 font-size-14">
                Edit Review
              </v-list-item-title>
            </v-list-item> -->

            <!-- <v-list-item
              link
              v-on:click="deleteReview()"
              :disabled="statusLoading"
            >
              <v-list-item-icon class="margin-auto-zero mr-3 my-2">
                <v-icon class="icon-default-blue">mdi-delete</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="font-weight-500 font-size-14">
                Delete Review
              </v-list-item-title>
            </v-list-item> -->

            <v-list-item
              link
              v-on:click="exportReview()"
              :disabled="statusLoading"
            >
              <v-list-item-icon class="margin-auto-zero mr-3 my-2">
                <v-icon class="icon-default-blue">mdi-download</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="font-weight-500 font-size-14">
                Export Review
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn
          depressed
          class="mx-2 mb-1 custom-grey-border custom-bold-button"
          v-on:click="goBack()"
        >
          <v-icon small left>mdi-keyboard-backspace</v-icon>
          Back
        </v-btn>
      </div>
    </template>

    <template v-slot:body>
      <v-container fluid class="">
        <v-row v-if="pageLoaded">
          <v-col md="12" class="py-0">
            <!-- Only Overview Tab Content -->
            <v-container fluid class="px-0">
              <!-- Customer Feedback Cards -->
              <v-row class="mb-4">
                <!-- Customer Rating Card -->
                <v-col md="6">
                  <v-card
                    flat
                    class="custom-grey-border remove-border-radius h-100"
                  >
                    <v-card-title class="headline grey lighten-4 py-1">
                      <span
                        class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase"
                      >
                        Customer Rating
                      </span>
                    </v-card-title>
                    <v-card-text class="p-6 font-size-16">
                      <table width="100%">
                        <tr>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                            width="140px"
                          >
                            Overall Rating
                          </td>
                          <td align="center" class="py-1" width="20px">:</td>
                          <td valign="middle" class="py-1 font-size-18">
                            <template v-if="reviewData.rating">
                              <v-rating
                                readonly
                                dense
                                v-model="reviewData.rating"
                                background-color="orange lighten-3"
                                color="orange"
                                size="20"
                              ></v-rating>
                              <span class="ml-2 font-weight-600"
                                >({{ reviewData.rating }}/5)</span
                              >
                            </template>
                            <em v-else class="text-muted">No rating</em>
                          </td>
                        </tr>
                        <tr>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            Punctuality
                          </td>
                          <td align="center">:</td>
                          <td valign="middle" class="py-1 font-size-18">
                            <template
                              v-if="reviewData.punctual_professional !== null"
                            >
                              <v-chip
                                small
                                :color="
                                  reviewData.punctual_professional
                                    ? 'green'
                                    : 'red'
                                "
                                text-color="white"
                                label
                              >
                                {{
                                  reviewData.punctual_professional
                                    ? "Yes"
                                    : "No"
                                }}
                              </v-chip>
                            </template>
                            <em v-else class="text-muted">Not rated</em>
                          </td>
                        </tr>
                        <tr>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            Cleaning Satisfaction
                          </td>
                          <td align="center">:</td>
                          <td valign="middle" class="py-1 font-size-18">
                            <template
                              v-if="reviewData.cleaning_satisfaction !== null"
                            >
                              <v-chip
                                small
                                :color="
                                  reviewData.cleaning_satisfaction
                                    ? 'green'
                                    : 'red'
                                "
                                text-color="white"
                                label
                              >
                                {{
                                  reviewData.cleaning_satisfaction
                                    ? "Yes"
                                    : "No"
                                }}
                              </v-chip>
                            </template>
                            <em v-else class="text-muted">Not rated</em>
                          </td>
                        </tr>
                        <tr>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            Would Recommend
                          </td>
                          <td align="center">:</td>
                          <td valign="middle" class="py-1 font-size-18">
                            <template v-if="reviewData.recommend !== null">
                              <v-chip
                                small
                                :color="reviewData.recommend ? 'green' : 'red'"
                                text-color="white"
                                label
                              >
                                {{ reviewData.recommend ? "Yes" : "No" }}
                              </v-chip>
                            </template>
                            <em v-else class="text-muted">Not answered</em>
                          </td>
                        </tr>
                      </table>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Customer Comments Card -->
                <v-col md="6">
                  <v-card
                    flat
                    class="custom-grey-border remove-border-radius h-100"
                  >
                    <v-card-title class="headline grey lighten-4 py-1">
                      <span
                        class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase"
                      >
                        Customer Comments
                      </span>
                    </v-card-title>
                    <v-card-text class="p-6 font-size-16">
                      <template v-if="reviewData.comments">
                        <div
                          class="pa-2"
                          style="background-color: #f5f5f5; border-radius: 4px"
                        >
                          <p
                            class="mb-0 font-size-16"
                            style="
                              line-height: 1.5;
                              color: #424242;
                              font-style: italic;
                            "
                          >
                            "{{ reviewData.comments }}"
                          </p>
                        </div>
                      </template>
                      <template v-else>
                        <div class="text-center py-4">
                          <v-icon color="grey lighten-1" size="36"
                            >mdi-comment-text-outline</v-icon
                          >
                          <p class="text-muted mt-2 mb-0">
                            No comments provided
                          </p>
                        </div>
                      </template>

                      <!-- Additional Feedback -->
                      <template v-if="reviewData.additional_feedback">
                        <v-divider class="my-3"></v-divider>
                        <div class="mb-2">
                          <strong>Additional Feedback:</strong>
                        </div>
                        <div
                          class="pa-2"
                          style="background-color: #f5f5f5; border-radius: 4px"
                        >
                          <p
                            class="mb-0 font-size-16"
                            style="line-height: 1.5; color: #424242"
                          >
                            {{ reviewData.additional_feedback }}
                          </p>
                        </div>
                      </template>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-row>
                <!-- Review Information -->
                <v-col md="6">
                  <v-card
                    flat
                    class="custom-grey-border remove-border-radius mb-4 h-100"
                  >
                    <v-card-title class="headline grey lighten-4 py-1">
                      <span
                        class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase"
                      >
                        Review Information
                      </span>
                    </v-card-title>
                    <v-card-text class="p-6 font-size-16">
                      <table width="100%">
                        <tr>
                          <td
                            valign="middle"
                            width="140px"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            Review ID
                          </td>
                          <td align="center" width="20px">:</td>
                          <td valign="middle" class="py-1 font-size-18">
                            <v-chip small color="cyan" text-color="white" label>
                              RW-{{ String(reviewData.id).padStart(4, "0") }}
                            </v-chip>
                          </td>
                        </tr>
                        <tr>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            Submitted Date
                          </td>
                          <td align="center">:</td>
                          <td valign="middle" class="py-1 font-size-18">
                            <label class="m-0">
                              {{ formatDate(reviewData.added_at) }}
                              {{ formatDateStartTime(reviewData.added_at) }}
                            </label>
                          </td>
                        </tr>
                        <tr
                          v-if="
                            reviewData.updated_at &&
                            reviewData.updated_at !== reviewData.added_at
                          "
                        >
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            Last Updated
                          </td>
                          <td align="center">:</td>
                          <td valign="middle" class="py-1 font-size-18">
                            <label class="m-0">
                              {{ formatDate(reviewData.updated_at) }}
                              {{ formatDateStartTime(reviewData.updated_at) }}
                            </label>
                          </td>
                        </tr>
                        <tr v-if="reviewData.user">
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            Added By
                          </td>
                          <td align="center">:</td>
                          <td valign="middle" class="py-1 font-size-18">
                            <label class="m-0">
                              {{ reviewData.user.display_name || "System" }}
                            </label>
                          </td>
                        </tr>
                      </table>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Customer Information -->
                <v-col md="6">
                  <v-card
                    flat
                    class="custom-grey-border remove-border-radius mb-4 h-100"
                  >
                    <v-card-title class="headline grey lighten-4 py-1">
                      <span
                        class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase"
                      >
                        Customer Information
                      </span>
                    </v-card-title>
                    <v-card-text class="p-6 font-size-16">
                      <div
                        v-if="reviewData.customer"
                        class="d-flex align-center mb-3"
                      >
                        <v-avatar size="48" class="mr-3">
                          <v-img
                            :src="
                              reviewData.customer.profile_image ||
                              $defaultProfileImage
                            "
                            :lazy-src="$defaultProfileImage"
                          ></v-img>
                        </v-avatar>
                        <div>
                          <div class="font-weight-600 font-size-18">
                            {{ reviewData.customer.display_name }}
                          </div>
                          <div class="text--secondary">
                            {{ reviewData.customer.primary_email }}
                          </div>
                          <div
                            class="text--secondary"
                            v-if="reviewData.customer.primary_phone"
                          >
                            {{ reviewData.customer.primary_phone }}
                          </div>
                        </div>
                      </div>
                      <template v-else>
                        <em class="text-muted"
                          >No customer information available</em
                        >
                      </template>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Job Description -->
              <v-card
                flat
                class="custom-grey-border remove-border-radius mb-4"
                v-if="reviewData.ticket"
              >
                <v-card-title class="headline grey lighten-4">
                  <span
                    class="font-weight-700 custom-headline color-custom-blue font-size-16"
                    >Job Description</span
                  >
                </v-card-title>
                <v-card-text class="p-6 font-size-16">
                  <read-more
                    class="mx-4 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                    more-str="read more"
                    :text="
                      reviewData.ticket.description || '<em>No Description</em>'
                    "
                    link="#"
                    less-str="read less"
                    :max-chars="200"
                  >
                  </read-more>
                </v-card-text>
              </v-card>
            </v-container>
          </v-col>
        </v-row>
      </v-container>

      <!-- Loaded State -->
      <v-container v-if="pageLoaded" fluid class="pa-4">
        <!-- actual content here -->
      </v-container>

      <!-- Loading State -->
      <v-container
        v-else
        fluid
        class="d-flex justify-center align-center"
        style="min-height: 400px"
      >
        <v-progress-circular
          :size="70"
          :width="7"
          color="cyan"
          indeterminate
        ></v-progress-circular>
      </v-container>
    </template>
  </DetailTemplate>
</template>

<script>
import DetailTemplate from "@/view/pages/partials/Detail-Template.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import JwtService from "@/core/services/jwt.service";

export default {
  name: "ReviewDetail",
  components: {
    DetailTemplate,
    Barcode,
  },
  data() {
    return {
      reviewData: {},
      pageLoaded: false,
      statusLoading: false,
      reviewId: null,
    };
  },
  created() {
    this.reviewId = this.$route.params.id;
    this.getReviewDetail();
  },
  computed: {
    filteredVisits() {
      return Array.isArray(this.reviewData?.ticket?.visit)
        ? this.reviewData.ticket.visit.filter((v) => v && v.barcode)
        : [];
    },
  },
  methods: {
    async getReviewDetail() {
      try {
        this.statusLoading = true;

        // Replace with your actual API endpoint
        const response = await this.$http.get(`reviews/${this.reviewId}`);

        if (response.data.success) {
          this.reviewData = response.data.data;
          this.pageLoaded = true;
        } else {
          this.$notify.error("Failed to load review details");
        }
      } catch (error) {
        console.error("Error fetching review:", error);
        this.$notify.error("Error loading review details");
      } finally {
        this.statusLoading = false;
      }
    },

    // getPermission(permission) {
    //   // Implement your permission check logic
    //   return true;
    // },

    getDefaultRoute(routeName, params = {}) {
      return {
        name: routeName,
        ...params,
      };
    },

    getRatingColor(rating) {
      if (rating >= 4) return "green";
      if (rating >= 3) return "orange";
      return "red";
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString();
    },

    formatDateStartTime(date) {
      if (!date) return "";
      return new Date(date).toLocaleTimeString();
    },

    goBack() {
      this.$router.go(-1);
    },

    // editReview() {
    //   // Implement edit review logic
    //   this.$router.push(`/reviews/${this.reviewId}/edit`);
    // },

    // deleteReview() {
    //   // Implement delete review logic
    //   if (confirm("Are you sure you want to delete this review?")) {
    //     // Add your delete API call here
    //     console.log("Delete review:", this.reviewId);
    //   }
    // },

    exportReview() {
      try {
        let downloadURL = process.env.VUE_APP_API_URL + "reviews/export";
        downloadURL = new URL(downloadURL);

        // Get token from multiple possible sources
        const token =
          this.$store.getters.getToken ||
          JwtService.getToken() ||
          localStorage.getItem("token") ||
          localStorage.getItem("jwt_token");

        if (!token) {
          this.$notify.error(
            "Authentication token not found. Please login again."
          );
          return;
        }

        downloadURL.searchParams.append("token", token);
        downloadURL.searchParams.append("review_id", this.reviewId);

        console.log("Export URL:", downloadURL.toString());

        const link = document.createElement("a");
        link.href = downloadURL.toString();
        link.target = "_blank";
        link.download = `review_${this.reviewId}_export.xlsx`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$notify.success("Export started successfully");
      } catch (error) {
        console.error("Export error:", error);
        this.$notify.error("Failed to export review");
      }
    },
  },
};
</script>
